/* -----------------Globals--------------- */
import { useSafeAreaInsets } from "react-native-safe-area-context";

/* -----------------UI--------------- */
import ReportCardsContainer from "@memorang/ui/components/SessionReport/ReportCardsContainer";
import ReviewAnswersDialog, {
	type CheckboxStateMap,
} from "./dialogs/ReviewAnswersDialog";

/* -----------------Helpers & Hooks--------------- */
import {
	getItemsPerformanceCardData,
	getReportData,
} from "../helpers/report-data";

/* -----------------Child components--------------- */

import { events } from "@constants/tracking";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { useAppVersion } from "@helpers/app-version";
import { getShowStudyPackViews } from "@helpers/exams";
import { useAppStore } from "@hooks/useAppStore";
import { useGlobalStore } from "@hooks/useGlobalStore";
import { SessionAnalysis } from "@memorang/features/src/session-analysis/SessionAnalysis";
import { formatDate } from "@memorang/helpers/date";
import { isWeb } from "@memorang/helpers/platform";
import type { BaseItemSchema } from "@memorang/types";
import type { ReportDetails } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import StudySessionItemsPerformanceCard from "@memorang/ui/components/Cards/StudySessionItemsPerformanceCard/StudySessionItemsPerformanceCard";
import { StudySessionMasteryTagProgressCard } from "@memorang/ui/components/Cards/StudySessionMasteryTagProgressCard";
import { StudySessionPerformanceCard } from "@memorang/ui/components/Cards/StudySessionPerformanceCard";
import StudySessionSummaryCard from "@memorang/ui/components/Cards/StudySessionSummaryCard";
import InfoAlert from "@memorang/ui/components/InfoAlert";
import ScrollContainer from "@memorang/ui/components/ScrollContainer";
import CardSkeleton from "@memorang/ui/components/Skeletons/CardSkeleton";
import { SuggestedActionsChips } from "@memorang/ui/components/SuggestedActionChips";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import { trackCustomEvent } from "analytics";
import { router } from "expo-router";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";

type Props = {
	reportDetails: ReportDetails;
	showReviewDialog: boolean;
	handleContinue: () => void;
	handleClickActionChip: (action: string) => void;
	checkboxStateMap: CheckboxStateMap;
	handleChangeCheckboxStateMap: (key: keyof CheckboxStateMap) => void;
	handleReviewButtonClick: () => void;
	handleCloseReviewDialog: () => void;
	selectedItemTypeForReview?: "QUESTION";
	handleReview: (checkboxStateMap: CheckboxStateMap) => void;
	fetchingReviewItems: boolean;
	isSummativeTest?: boolean;
	handleUpdateIncorrectCheckboxStateMap: () => void;
	handleTracking: (
		eventName: string,
		params?: Record<string, string | number>,
	) => void;
	sessionItems?: BaseItemSchema[];
	loadingSessionItems?: boolean;
	shouldRenderItemReviewCard?: boolean;
};
export const ReportView = ({
	reportDetails,
	showReviewDialog,
	handleContinue,
	checkboxStateMap,
	isSummativeTest,
	handleChangeCheckboxStateMap,
	handleReviewButtonClick,
	handleCloseReviewDialog,
	selectedItemTypeForReview,
	handleReview,
	fetchingReviewItems,
	handleTracking,
	handleUpdateIncorrectCheckboxStateMap,
	handleClickActionChip,
	sessionItems,
	loadingSessionItems,
	shouldRenderItemReviewCard,
	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
}: Props) => {
	const { t, i18n } = useTranslation();
	const theme = useAppTheme();

	const {
		currentExamContext: {
			examName,
			studyPackCollectionId,
			examId,
			projectId,
			organizationId,
		},
	} = useExamContext();
	const {
		summaryCardData,
		masteryCardData,
		masteryTagProgressData,
		reviewListItemsData,
		sessionId,
	} = getReportData(
		reportDetails,
		t,
		theme,
		isSummativeTest,
		studyPackCollectionId,
	);

	const tagTypeLabel = useAppStore((store) => store.app.tagType);
	const isPremiumUser = useGlobalStore((store) => store.isPremiumUser);
	const practiceTestEnabled = useAppStore(
		(store) => store.exam.practiceTest.enabled,
	);
	const appId = useAppStore((store) => store.app.id);
	const appVersion = useAppVersion();
	const showFullReport = !reportDetails?.sessionSummaryConfig?.length;

	const { username, email } = useAuthContext();

	const showStudyPackViews = getShowStudyPackViews(studyPackCollectionId);

	const { isMobile } = useDevice();

	const showPointsBasedProgress = useAppStore(
		(store) => store.reportView?.formative?.showPointsBasedScore,
	);
	const companyName = useAppStore((store) => store.companyName);
	const appName = useAppStore((store) => store.app.name);
	const scoreRanges = useAppStore((store) => store.app.scoreRanges);
	const tenantName = useAppStore((store) => store.tenant.name);
	const reportView = useAppStore((store) => store.reportView);
	const sessionAnalysisEnabled = Boolean(
		useAppStore((store) => store.reportView?.ai?.sessionAnalysis),
	);
	const appFont = useAppStore((app) => app.app.font);
	const fontSize = useAppStore((store) => store.app.fontSizes?.stem) || 16;

	const reviewEnabled =
		reportView?.practiceTest?.reviewEnabled &&
		!reportDetails.isMigratedProgress &&
		!reportDetails.legacySession;

	const studyPackReviewEnabled = reportView?.formative?.reviewEnabled;

	const productName = t("report.productName", {
		examName,
		type: isSummativeTest
			? studyPackCollectionId === 38
				? t("report.diagnosticSessionCompleted")
				: t("report.practiceTestCompleted")
			: t("report.studyPack"),
	});

	const insets = useSafeAreaInsets();

	const selectedReviewListItemsData =
		reviewListItemsData?.[selectedItemTypeForReview!];

	const handleClickCard = (title: string) => {
		trackCustomEvent({
			eventName: events.sessionReportCardClicked,
			sessionId: sessionId,
			type: title,
		});
		router.push({
			pathname: "/(protected)/session/[id]/report/detailed-list/mastery",
			params: {
				type: title,
				id: sessionId,
				examName: reportDetails?.examName,
			},
		});
	};
	const masteryTagsCards = masteryTagProgressData?.map((item) => {
		return (
			<StudySessionMasteryTagProgressCard
				key={item.title}
				{...item}
				handleClickCard={() => handleClickCard(item.tagType!)}
			/>
		);
	});

	const getReviewEnabled = () => {
		switch (reportDetails?.reportSessionType) {
			case "DIAGNOSTIC":
			case "SUMMATIVE":
				return isWeb ? reviewEnabled : practiceTestEnabled?.mobile;
			case "PRACTICETEST":
				return reviewEnabled && (isWeb ? true : !isMobile);
			case "FORMATIVE":
				return studyPackReviewEnabled;
			default:
				return false;
		}
	};
	const showReview = getReviewEnabled();

	const handleNavigateToItemsPerformance = () => {
		router.push({
			pathname: "/(protected)/session/[id]/report/items",
			params: { id: sessionId },
		});
	};

	const cards = [
		<StudySessionSummaryCard
			{...summaryCardData}
			handleContinue={handleContinue}
			scoreRanges={scoreRanges!}
			isSummative={isSummativeTest}
			showScoreRanges={!showPointsBasedProgress}
			isSummativeTest={isSummativeTest}
			key={`${sessionId}-summary-card`}
			infoComp={
				summaryCardData.sessionReportInfoText ? (
					<InfoAlert
						text={summaryCardData.sessionReportInfoText}
						backgroundColor={theme.palette.info.main}
					/>
				) : null
			}
		/>,
		...(showFullReport
			? [
					<Box
						paddingTop={16}
						key={`${sessionId}-suggested-actions-chips-container`}
					>
						<SuggestedActionsChips
							showReview={showReview}
							showStudy={showStudyPackViews}
							handleClickActionChip={handleClickActionChip}
							tagTypeLabel={tagTypeLabel[i18n.language]}
							isPremiumUser={isPremiumUser}
						/>
					</Box>,
				]
			: []),

		...(examId && !reportDetails.isMigratedProgress && sessionAnalysisEnabled
			? [
					<SessionAnalysis
						fontSize={fontSize}
						appFont={appFont}
						sessionId={Number(sessionId)}
						examId={examId}
						appId={appId}
						context={examName}
						handleTracking={handleTracking}
						sessionMetadata={{
							sessionTitle: reportDetails.examName,
							sessionDate: formatDate(reportDetails.sessionDate),
							numItems: reportDetails.numQuestions,
						}}
						outputLanguage={i18n.language}
						key={`${sessionId}-session-analysis`}
						feedbackDialogParams={{
							currentAppVersion: appVersion,
							userName: username ?? "",
							email,
							projectId: projectId,
							organizationId: organizationId,
							appLanguage: i18n.language,
							product: productName,
							appName,
							platform: Platform.OS,
							view: "Session Report",
							tenantId: tenantName,
							context: examName,
							feature: "AI Session Analysis",
							sessionId: `${sessionId}`,
							companyName,
						}}
					/>,
				]
			: []),
		...(masteryCardData && showFullReport
			? [
					<StudySessionPerformanceCard
						checkboxStateMap={checkboxStateMap}
						handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
						{...masteryCardData}
						handleReviewButtonClick={handleReviewButtonClick}
						showReview={showReview}
						key={`${sessionId}-mastery-card`}
					/>,
				]
			: []),
		...(shouldRenderItemReviewCard && showFullReport
			? loadingSessionItems || !sessionItems
				? [
						<CardSkeleton
							key={`${sessionId}-items-performance-card`}
							numRows={3}
						/>,
					]
				: sessionItems.length
					? [
							<StudySessionItemsPerformanceCard
								key={`${sessionId}-items-performance-card`}
								{...getItemsPerformanceCardData(sessionItems)}
								handleClickCard={handleNavigateToItemsPerformance}
							/>,
						]
					: []
			: []),
		...masteryTagsCards,
	];

	return (
		<ScrollContainer
			contentContainerStyle={{
				paddingTop: isSummativeTest ? 84 : 58,
				paddingBottom: 88 + insets.bottom,
				paddingHorizontal: 16,
				gap: 16,
			}}
		>
			<ReportCardsContainer cards={cards} />
			{showReviewDialog && (
				<ReviewAnswersDialog
					open={showReviewDialog}
					handleChangeCheckboxStateMap={handleChangeCheckboxStateMap}
					checkboxStateMap={checkboxStateMap}
					sections={selectedReviewListItemsData?.items?.sections!}
					handleClose={handleCloseReviewDialog}
					handleReview={handleReview}
					loading={fetchingReviewItems}
					numIncorrect={selectedReviewListItemsData?.numIncorrect}
					handleUpdateIncorrectCheckboxStateMap={
						handleUpdateIncorrectCheckboxStateMap
					}
				/>
			)}
		</ScrollContainer>
	);
};
